/** Products — Fabric's shapes, standing in for the API (stage A).
 *
 *  These are FABRIC'S models, unchanged. A `Product` here is the thing a
 *  company is granted access to — it carries spaces, permission maps, reference
 *  files and tool releases. That it also happens to name a piece of hardware is
 *  a coincidence of Fabric's history, not a definition, and untangling the two
 *  is stage C (§9f). Nothing here anticipates that.
 *
 *  Likewise `SpaceDefinition` is ported as-is. Spaces are on the stage-C list;
 *  porting and redesigning at once means a failure has two possible causes.
 */

export type Product = {
  product_id: string;      // slug, used in URLs and the audit log
  product_code: string;    // PRD-001, display only — assigned at create
  name: string;
  icon: string | null;
  description: string | null;
  status: "active" | "beta" | "hidden" | "inactive";
  spaces: number;
  companies: number;
};

/** Per-product space catalogue. `company_id` null = a global default that any
 *  company may be allocated; set = private to one company. */
export type SpaceDefinition = {
  space_id: number;
  product_id: string;
  slug: string;
  name: string;
  description: string | null;
  /** null = global default. */
  private_to: string | null;
  archived: boolean;
  /** How many companies currently hold this space. */
  allocations: number;
};

/** One of three fixed reference slots per product, at product (default) level.
 *  A company may override it; those overrides live on the organisation. */
export type ProductReferenceFile = {
  ref_file_id: number;
  product_id: string;
  ref_type: "app_config_did" | "mfg_config_did" | "data_upload_signals";
  filename: string;
  size_bytes: number;
  uploaded_by: string;
  uploaded_at: string;
  archived: boolean;
  /** How many companies have overridden this slot. */
  overrides: number;
};

/** One uploaded installer build for a (product, tool, platform).
 *  `version_code` is the comparable version; `version_name` is display only. */
export type ToolRelease = {
  release_id: number;
  product_id: string;
  tool_slug: string;
  platform: "windows";
  version_code: number;
  version_name: string;
  channel: "stable" | "beta";
  filename: string;
  size_bytes: number;
  /** Returned to the client so it can verify the installer BEFORE running it. */
  sha256: string;
  notes: string | null;
  status: "draft" | "published" | "archived";
  uploaded_by: string;
  uploaded_at: string;
};

/** Targets a release, and optionally a hard floor, at an audience.
 *  Resolution picks the most specific ENABLED rule: user > company > role >
 *  global. A floor makes the update mandatory below it. */
export type RolloutRule = {
  rule_id: number;
  product_id: string;
  tool_slug: string;
  platform: "windows";
  scope_type: "global" | "company" | "role" | "user";
  /** Whichever of company / role / user the scope names; null for global. */
  scope_value: string | null;
  /** The release offered to this audience. */
  offer_version: number | null;
  /** Below this, the client MUST update. */
  floor_version: number | null;
  enabled: boolean;
};

export const REF_TYPE_LABEL: Record<ProductReferenceFile["ref_type"], string> = {
  app_config_did: "App Config DID",
  mfg_config_did: "Mfg Config DID",
  data_upload_signals: "Historic data signals",
};

export const REF_TYPE_DESC: Record<ProductReferenceFile["ref_type"], string> = {
  app_config_did: "Defines the App Config signal map the tool reads and writes.",
  mfg_config_did: "The Mfg Config signal map written during manufacturing — it carries the electronics serial number.",
  data_upload_signals: "Compiled nanopb schema the tool uses to decode historic data-log records.",
};

// ---------------------------------------------------------------- the data --

export const PRODUCTS: Product[] = [
  { product_id: "cluster_meter", product_code: "PRD-001", name: "Cluster Meter", icon: null,
    description: "Segment cluster, unconnected", status: "active", spaces: 3, companies: 2 },
  { product_id: "connected_cluster", product_code: "PRD-002", name: "Connected Cluster", icon: null,
    description: "TFT cluster with telematics", status: "active", spaces: 2, companies: 3 },
  { product_id: "orbit_platform", product_code: "PRD-003", name: "Orbit Platform", icon: null,
    description: "Fleet and registry console", status: "active", spaces: 1, companies: 4 },
];

export const SPACES: SpaceDefinition[] = [
  { space_id: 1, product_id: "cluster_meter", slug: "eol", name: "EOL",
    description: "End-of-line production bench", private_to: null, archived: false, allocations: 2 },
  { space_id: 2, product_id: "cluster_meter", slug: "rnd_testing", name: "R&D testing",
    description: "Engineering bench — test builds", private_to: null, archived: false, allocations: 1 },
  { space_id: 3, product_id: "cluster_meter", slug: "nexcell_line_2", name: "Nexcell line 2",
    description: null, private_to: "CMP-011", archived: false, allocations: 1 },
  { space_id: 4, product_id: "connected_cluster", slug: "eol", name: "EOL",
    description: "End-of-line production bench", private_to: null, archived: false, allocations: 3 },
  { space_id: 5, product_id: "connected_cluster", slug: "rnd_testing", name: "R&D testing",
    description: "Engineering bench", private_to: null, archived: false, allocations: 1 },
  { space_id: 6, product_id: "orbit_platform", slug: "development", name: "Development",
    description: "The only space — Orbit does not partition", private_to: null, archived: false, allocations: 4 },
];

export const PRODUCT_REF_FILES: ProductReferenceFile[] = [
  { ref_file_id: 301, product_id: "connected_cluster", ref_type: "app_config_did",
    filename: "app_config_did_v4.xlsx", size_bytes: 24_576, uploaded_by: "krisha.chhadawa@aphelion.in",
    uploaded_at: "2026-08-14", archived: false, overrides: 0 },
  { ref_file_id: 302, product_id: "connected_cluster", ref_type: "mfg_config_did",
    filename: "mfg_config_did_v3.xlsx", size_bytes: 21_504, uploaded_by: "krisha.chhadawa@aphelion.in",
    uploaded_at: "2026-08-14", archived: false, overrides: 1 },
  { ref_file_id: 303, product_id: "connected_cluster", ref_type: "data_upload_signals",
    filename: "historic_signals_v7.desc", size_bytes: 9_216, uploaded_by: "ravi@aphelion.in",
    uploaded_at: "2026-09-02", archived: false, overrides: 0 },
];

export const TOOL_RELEASES: ToolRelease[] = [
  { release_id: 41, product_id: "connected_cluster", tool_slug: "uds_tool", platform: "windows",
    version_code: 241, version_name: "2.4.1", channel: "stable", filename: "uds-tool-2.4.1-setup.exe",
    size_bytes: 88_129_536, sha256: "4f1c9a…b207", notes: "Flash reporting fixes", status: "published",
    uploaded_by: "ravi@aphelion.in", uploaded_at: "2026-09-16" },
  { release_id: 42, product_id: "connected_cluster", tool_slug: "uds_tool", platform: "windows",
    version_code: 250, version_name: "2.5.0-beta1", channel: "beta", filename: "uds-tool-2.5.0b1-setup.exe",
    size_bytes: 89_178_112, sha256: "aa70d2…91ce", notes: "Board ID capture at EOL", status: "draft",
    uploaded_by: "ravi@aphelion.in", uploaded_at: "2026-09-24" },
  { release_id: 39, product_id: "connected_cluster", tool_slug: "uds_tool", platform: "windows",
    version_code: 220, version_name: "2.2.0", channel: "stable", filename: "uds-tool-2.2.0-setup.exe",
    size_bytes: 84_934_656, sha256: "17b3ee…4d80", notes: null, status: "archived",
    uploaded_by: "ravi@aphelion.in", uploaded_at: "2026-06-30" },
];

export const ROLLOUT_RULES: RolloutRule[] = [
  { rule_id: 7, product_id: "connected_cluster", tool_slug: "uds_tool", platform: "windows",
    scope_type: "global", scope_value: null, offer_version: 241, floor_version: 240, enabled: true },
  { rule_id: 8, product_id: "connected_cluster", tool_slug: "uds_tool", platform: "windows",
    scope_type: "company", scope_value: "CMP-001", offer_version: 250, floor_version: null, enabled: true },
  { rule_id: 9, product_id: "connected_cluster", tool_slug: "uds_tool", platform: "windows",
    scope_type: "user", scope_value: "ravi@aphelion.in", offer_version: 250, floor_version: null, enabled: false },
];

export function useProducts(): Product[] { return PRODUCTS; }
export function useSpaces(productId: string): SpaceDefinition[] {
  return SPACES.filter((s) => s.product_id === productId);
}
export function useProductRefFiles(productId: string): ProductReferenceFile[] {
  return PRODUCT_REF_FILES.filter((r) => r.product_id === productId);
}
export function useToolReleases(productId: string): ToolRelease[] {
  return TOOL_RELEASES.filter((r) => r.product_id === productId);
}
export function useRolloutRules(productId: string): RolloutRule[] {
  return ROLLOUT_RULES.filter((r) => r.product_id === productId);
}
